import classNames from 'classnames';
import { NETWORK_ERROR_MODAL_TITLE } from '@mm/constants/error-descriptions';
import { useChatbotCRUDActions } from '@mm/state/actions/ChatbotCRUDActions';
import { getErrorText } from '@mm/state/errorHandlers/common-handler';
import ChatbotModel from '@mm/state/model/ChatbotModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { getErrorJson } from '@mm/utils';
import React, { useState } from 'react';
import Switch from '../Switch/Switch';
import './ToggleBotLive.pcss';
import { UserRoleContainer } from '../../hooks/use-route-guard';
import somethingWentWrong from 'assets/something-went-wrong.png';

export const getConnectedPage = (activeBot: ChatbotModel) => {
  const { connectedPages } = activeBot;
  return (connectedPages && connectedPages[0]) ?? {};
};

type Props = {
  bot: ChatbotModel;
  className?: string;
  onToggle?: () => void;
  onSuccess?: () => void;
  onReject?: () => void;
};

export const ToggleBotLive = ({
  bot,
  className,
  onToggle = () => {},
  onSuccess = () => {},
  onReject = () => {},
}: Props) => {
  const {
    roleFlags: { isLiveChatAgent },
  } = UserRoleContainer.useContainer();

  const { attachToFacebookPage } = useChatbotCRUDActions();
  const { openConfirmChangeBotStatusModal, openPrettyErrorModal } =
    useModalActions();
  const connectedPage = getConnectedPage(bot);
  const isLive = connectedPage && connectedPage.activeBotId === bot.id;
  const isTemplate = bot.template;
  // To deactivate needs to be null
  const botIdToPass = isLive ? null : bot.id;
  const statusText = isLive ? 'Active' : 'Inactive';
  const templateStatusText = 'Cannot activate a template';
  const isAlreadyConnected =
    !isLive && connectedPage && connectedPage.activeBotId;

  const [busy, setBusy] = useState(false);

  const attachFunction = async () => {
    setBusy(true);
    onToggle();
    if (isAlreadyConnected) {
      await openConfirmChangeBotStatusModal(connectedPage, bot.id);
      onSuccess();
    } else {
      try {
        await attachToFacebookPage(botIdToPass, connectedPage.id, true);
        setBusy(false);
        onSuccess();
      } catch (err) {
        setBusy(false);
        onReject();
        const detailsForNerds =
          err instanceof Response
            ? (await getErrorJson(err)).errors?.[0]
            : await getErrorText(err);

        await openPrettyErrorModal({
          title: NETWORK_ERROR_MODAL_TITLE,
          detailsForNerds,
          contactSupport: true,
          image: somethingWentWrong,
          onClose: () => window?.location.reload(),
        });
      }
    }
  };

  return (
    <div className={classNames(className, 'toggle-bot-live')}>
      <Switch
        checked={isLive}
        onChange={attachFunction}
        disabled={isTemplate || busy || isLiveChatAgent}
      />
      <div className="toggle-bot-live__switch-text">
        {isTemplate && templateStatusText}
      </div>
    </div>
  );
};
